import { Row } from 'app/design/primitives/box';
import { type CategoryIcons, CategoryTab } from 'app/design/categories/tab';
import { Button, PressableType } from 'app/design/primitives/button';
import { Text } from 'app/design/primitives/text';

import { useNavigation } from 'app/hooks/navigation';
import { getI18nLanguage } from 'app/lib/language';
import { useAllCategories, useAllServiceCategories } from 'app/network/category';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { createTheme as createDSTheme } from 'app/shopify';
import { IconChevronRight } from '@tabler/icons-react-native';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
const theme = createDSTheme();

export function CategoryPicker({
  isService,
  selectedCategoryId,
  goBackTo,
  listingId,
  onPress,
}: {
  isService: boolean;
  selectedCategoryId?: string | null;
  goBackTo: 'new' | 'one-listing' | 'edit-listing' | 'set-atom-then-close';
  listingId?: string;
  onPress?: () => void;
}) {
  const { t } = useTranslation('new');
  const i18nLanguage = getI18nLanguage();
  const navigation = useNavigation();

  const { data: serviceCategories } = useAllServiceCategories();
  const { data: categories } = useAllCategories();

  const selectedCategory = isService
    ? serviceCategories?.map((c) => ({ ...c, icon: undefined })).find((c) => c.id === selectedCategoryId)
    : categories
        ?.map((c) => ({ id: c.id, name: c.name[i18nLanguage], icon: c.icon }))
        .find((c) => c.id === selectedCategoryId);

  const navigateToServiceCategory = () => {
    navigation.navigate('serviceCategories', { goBackTo: goBackTo, listingId }, { pop: true });
  };

  const navigateToCategorySelection = () => {
    navigation.navigate('categories', { goBackTo: goBackTo, listingId }, { pop: true });
  };

  return (
    <Button
      onPress={onPress ?? (isService ? navigateToServiceCategory : navigateToCategorySelection)}
      pressableType={PressableType.GestureHandler}
      variant="plain"
    >
      <Row
        alignItems="center"
        borderColor="border.secondary"
        borderRadius="radius.lg"
        borderWidth={1}
        flex={1}
        padding="spacing.general.x2"
        style={{
          height: 48,
          borderRadius: 24,
          backgroundColor: selectedCategory ? theme.colors['surface.inverse.tertiary-neutral'] : undefined,
        }}
        width="100%"
      >
        {selectedCategory ? (
          <CategoryTab category={selectedCategory.icon as CategoryIcons} size={24} />
        ) : (
          <CategoryTab category="other" color={theme.colors['text.placeholder']} size={24} />
        )}
        <Text flex={1} fontSize={16} fontWeight={selectedCategory ? '700' : undefined} paddingLeft="spacing.general.x2">
          {selectedCategory?.name
            ? isService
              ? t(`serviceCategories.${selectedCategory.name}.name`, { ns: 'common' })
              : selectedCategory.name
            : t('categorySection.primary.placeholder')}
        </Text>
        <IconChevronRight strokeWidth={ICON_STROKE_WIDTH} size={24} color={theme.colors['text.primary']} />
      </Row>
    </Button>
  );
}
